import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SectionHeader, StatCard } from "@/components/kit";
import { Users, Wine, GlassWater, Printer, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

const TIMELINE = ["4 weeks out: Finalize guest count & theme", "2 weeks out: Order bottles for delivery/pickup", "1 week out: Stock mixers, garnishes & ice", "Day before: Chill bottles & prep batches", "Day of: Set up bar station & glassware"];

export default function Planner() {
  const [themes, setThemes] = useState([]);
  const [guests, setGuests] = useState(24);
  const [hours, setHours] = useState(4);
  const [checked, setChecked] = useState({});
  useEffect(() => { api.get("/party-themes").then((r) => setThemes(r.data)); }, []);

  const drinks = Math.ceil(guests * hours * 1.2);
  const bottlesSpirits = Math.ceil(drinks / 16);
  const wine = Math.ceil(guests * 0.5);
  const ice = Math.ceil(guests * 1.5);
  const list = [`${bottlesSpirits} bottles of spirits (750ml)`, `${wine} bottles of wine`, `${Math.ceil(guests / 3)} six-packs of beer`, `${ice} lbs of ice`, `${Math.ceil(guests / 4)} citrus (limes/lemons)`, "Mixers, syrups & bitters", "Garnishes & napkins"];

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 lg:px-6">
      <SectionHeader overline="Host with confidence" title="Party planner" />

      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="font-display text-2xl">Guest & drink calculator</h3>
          <div className="mt-4 space-y-4">
            <label className="block text-sm"><span className="flex justify-between"><span className="flex items-center gap-1"><Users size={14} /> Guests</span><span className="font-semibold text-primary">{guests}</span></span><input data-testid="planner-guests" type="range" min="2" max="150" value={guests} onChange={(e) => setGuests(+e.target.value)} className="mt-1 w-full accent-[hsl(var(--primary))]" /></label>
            <label className="block text-sm"><span className="flex justify-between"><span>Event length (hours)</span><span className="font-semibold text-primary">{hours}h</span></span><input data-testid="planner-hours" type="range" min="1" max="10" value={hours} onChange={(e) => setHours(+e.target.value)} className="mt-1 w-full accent-[hsl(var(--primary))]" /></label>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3">
            <StatCard icon={GlassWater} label="Drinks" value={`~${drinks}`} />
            <StatCard icon={Wine} label="Spirit btls" value={bottlesSpirits} />
            <StatCard icon={Users} label="Ice (lbs)" value={ice} />
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center justify-between"><h3 className="font-display text-2xl">Shopping checklist</h3><button data-testid="print-checklist" onClick={() => window.print()} className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover-underline"><Printer size={14} /> Print</button></div>
          <ul className="mt-4 space-y-2">
            {list.map((item, i) => (
              <li key={i}><button data-testid={`check-${i}`} onClick={() => setChecked((s) => ({ ...s, [i]: !s[i] }))} className="flex w-full items-center gap-2 text-left text-sm">{checked[i] ? <CheckSquare size={16} className="text-primary" /> : <Square size={16} className="text-muted-foreground" />}<span className={checked[i] ? "text-muted-foreground line-through" : ""}>{item}</span></button></li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-10">
        <SectionHeader overline="Themed bar setups" title="Recipe bundles by party type" />
        <div className="grid grid-cols-2 gap-5 lg:grid-cols-3">
          {themes.map((t) => (
            <div key={t.id} data-testid={`theme-${t.id}`} className="card-lift overflow-hidden rounded-xl border border-border bg-card">
              <div className="relative aspect-[16/10] overflow-hidden"><img src={t.image} alt="" className="h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" /><span className="absolute bottom-3 left-3 font-display text-2xl text-white">{t.name}</span></div>
              <div className="p-4">
                <p className="text-sm text-muted-foreground">{t.desc}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">{t.recipe_objs?.map((r) => <Link key={r.id} to={`/recipes/${r.id}`} className="rounded-full bg-muted px-2.5 py-1 text-xs hover:text-primary">{r.name}</Link>)}</div>
                <button onClick={() => toast.success(`${t.name} plan saved`)} className="mt-3 w-full rounded-full border border-border py-2 text-sm font-semibold hover:border-primary hover:text-primary">Use this setup</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 rounded-xl border border-border bg-card p-6">
        <SectionHeader overline="Stay on track" title="Timeline checklist" />
        <ol className="space-y-3">{TIMELINE.map((t, i) => <li key={i} className="flex gap-3 text-sm"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">{i + 1}</span><span className="pt-0.5">{t}</span></li>)}</ol>
      </div>
    </div>
  );
}
